// v2.1 faces: the landing (E2) still names them until it moves to the brand tokens
import "@fontsource-variable/big-shoulders-display";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import { lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { AppErrorBoundary } from "./components/ErrorBoundary";
import { setUserSource } from "./monitoring";
import { boot } from "./session";
import { get, restoreTranscript, set, subscribeStore, toast } from "./store";
import { clearChats, loadChat, saveChat } from "./chatpersist";
import { setAnalyticsUser } from "./analytics";
// v3 brand (web/src/brand): the tokens first, the app's stylesheets, then the bridge that maps the app's v2.1
// names (--ink, --paper, --tungsten...) onto the brand roles. The display face is one token (--cao-font-display):
// a placeholder until the brand concept round settles it, so nothing here depends on its width axis
import "@fontsource-variable/anybody/standard.css"; // the placeholder display face (brand.md section 5)
import "@fontsource-variable/instrument-sans/standard.css";
import "@fontsource-variable/martian-mono/standard.css";
import "./brand/tokens.css";
import "./styles.css";
import "./styles-v2.css";
import "./styles-v21.css";
import "./styles-v3.css";
import "./styles-keylight.css";
import "./styles-v4-disclosure.css"; // v4 (V4-P1): Fold, Notice, NextStep
import "./styles-v4-suno.css"; // v4 (V4-M1): Make it in Suno
import "./styles-v4-tree.css"; // v4 (V4-H2): Home as the channel root, collections, the rail tree, Channel settings
import "./styles-v4-characters.css"; // v4 (V4-C4): the Character screen, cast cards, older-look markers
import "./styles-v4-showcase.css"; // v4 (V4-MS): the model showcase grid and side-by-side clips
import "./styles-v4-showcase-images.css"; // MS-IMG: the image-model showcase (strips, stills, large view)
import "./styles-v4-references.css"; // v4 (V4-R2): the References fold in a scene detail
import "./styles-v4-compare.css"; // v4 (V4-MC): compare models on a scene
import "./styles-media.css"; // phase 2: card posters + previews, the ••• menu, Change thumbnail
import "./styles-downloads.css"; // V4-DL: the Download files sheet
import "./styles-v4-library.css"; // v4 (V4-A2): the library at every level + the upload sheet
import "./styles-v4-help.css"; // v4 (D7 + V4-SP): the how_do_i highlight, Settings > Spending, Admin > Feedback
import "./styles-v4-history.css"; // v4 (V4-U1): the History fold and the Undo toast
import "./styles-v4-review.css"; // v4 (T4): review a finished version: player, lanes, notes, drawing, safe zones
import "./styles-v4-share.css"; // v4 (V4-SH): the share sheet, the share page, admin reports
import "./styles-v4-folds.css"; // v4 (V4-P2): the Fold adopted across the app (scene detail, brief, Versions, Settings…)
import "./styles-world.css"; // 2026-11-02: a video's World (characters, places, props)
import "./styles-v4-showcase-admin.css"; // MS-UI: Admin > Showcase (the model showcase job)
import "./styles-v4-parity.css"; // v4 (V4-D6): the taps added for parity: Stop/Try again, Purchases, Help and your data, Model rules, scene structure
import "./styles-v4-price.css"; // PRICE-FIRST: prices on paid buttons
import "./styles-report.css"; // 2026-11-02: the report button
import "./styles-personspend.css"; // 2026-11-02: Admin > People > an account > Spend
import "./brand/tokens-compat.css";
import "./pwa/pwa.css"; // the installable app: safe areas, install and update prompts
import { listenForInstallPrompt } from "./pwa/install";
import { registerServiceWorker } from "./pwa/register";

// The ElevenLabs SDK (with LiveKit) is the heaviest part of the app, so the Director's live link loads as its own
// chunk beside the UI, starting right away; tapping the iris before it arrives just waits for it.
const DirectorHost = lazy(() => import("./directorBridge"));

// the installable app: Chrome fires its install prompt once, early; the service worker is production-only
listenForInstallPrompt();
registerServiceWorker();

async function start() {
  // VITE_MOCK=1: in-memory API + simulated Director. The literal check lets production builds drop the mock entirely.
  const mock = import.meta.env.VITE_MOCK === "1";
  if (mock) (await import("./mock")).installMock();

  // ?lights=1 (mock only): the Director lights review sheet (src/mock/lights.tsx)
  if (mock && new URLSearchParams(location.search).get("lights") === "1") {
    const { LightsSheet } = await import("./mock/lights");
    createRoot(document.getElementById("root")!).render(<LightsSheet />);
    return;
  }

  // ?landing=1 (mock only): the signed-out landing page by itself, for screenshots (src/landing, spec-v2.1 C)
  if (mock && new URLSearchParams(location.search).get("landing") === "1") {
    const { Landing } = await import("./landing/Landing");
    createRoot(document.getElementById("root")!).render(<Landing onSignIn={() => location.assign("/login?out=1")} onRequestInvite={() => location.assign("/login?out=1")} />);
    return;
  }

  // Sentry (when /api/config turns it on) tags errors with the signed-in user's id, read at send time
  setUserSource(() => get().me?.user.id ?? null);
  // PostHog (when /api/config turns it on) starts at sign-in, identified by the user's id only, and resets at sign-out
  subscribeStore(() => setAnalyticsUser(get().me?.user.id ?? null));
  installChatPersistence();

  createRoot(document.getElementById("root")!).render(
    <AppErrorBoundary>
      {!mock && (
        <Suspense fallback={null}>
          <DirectorHost />
        </Suspense>
      )}
      <App />
    </AppErrorBoundary>,
  );

  try {
    await boot();
  } catch (e) {
    set({ auth: "out" });
    toast(`Couldn't reach CAO Studio. ${(e as Error).message}`, "bad");
  }
}
void start();

// 2026-11-02: the chat survives a reload (chatpersist.ts): restored once the user is known, saved as it changes (at
// most twice a second), cleared on sign-out
function installChatPersistence() {
  let user: string | null = null;
  let lastSaved: unknown = null;
  let timer: number | null = null;
  subscribeStore(() => {
    const s = get();
    const id = s.me?.user.id ?? null;
    if (s.auth === "out" && user) {
      clearChats();
      user = null;
      return;
    }
    if (id && id !== user) {
      user = id;
      restoreTranscript(loadChat(id));
      lastSaved = get().transcript;
      return;
    }
    if (!user || s.transcript === lastSaved || timer !== null) return;
    timer = window.setTimeout(() => {
      timer = null;
      const t = get().transcript;
      if (user && t !== lastSaved) {
        lastSaved = t;
        saveChat(user, t);
      }
    }, 500);
  });
  window.addEventListener("pagehide", () => user && saveChat(user, get().transcript));
}
